// ✅ src/lib/auth.js (FULL - POPUP ONLY)
// ✅ Uses popup everywhere (NO redirect)
// ✅ Still blocks in-app browsers and broken storage
// ✅ If iOS A2HS blocks popup, we show a clear message telling them to open Safari

import { auth } from "./firebase";
import {
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  reload,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";

const googleProvider = new GoogleAuthProvider();

/* ---------------- persistence ---------------- */

async function ensurePersistence() {
  try {
    await setPersistence(auth, browserLocalPersistence);
    return "local";
  } catch {
    try {
      await setPersistence(auth, browserSessionPersistence);
      return "session";
    } catch {
      return "none";
    }
  }
}

/* ---------------- environment checks ---------------- */

function canUseSessionStorage() {
  try {
    sessionStorage.setItem("__t", "1");
    sessionStorage.removeItem("__t");
    return true;
  } catch {
    return false;
  }
}

function isIOS() {
  try {
    const ua = navigator.userAgent || "";
    return /iPhone|iPad|iPod/i.test(ua);
  } catch {
    return false;
  }
}

function isInAppBrowser() {
  try {
    const ua = navigator.userAgent || "";
    return /FBAN|FBAV|Instagram|Line|Twitter|WhatsApp|wv/i.test(ua);
  } catch {
    return false;
  }
}

function isStandaloneMode() {
  try {
    const iosStandalone = window.navigator.standalone === true;
    const displayModeStandalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches;
    return iosStandalone || displayModeStandalone;
  } catch {
    return false;
  }
}

/**
 * ✅ Call once on app startup (index.js)
 */
export async function initAuth() {
  const mode = await ensurePersistence();
  return mode;
}

/**
 * ✅ Google login — POPUP ONLY
 *
 * RETURNS:
 * - { user, isNewUser }
 */
export async function loginWithGoogle() {
  // ✅ DO NOT await anything here before popup
  googleProvider.setCustomParameters({ prompt: "select_account" });

  const storageOk = canUseSessionStorage();
  const inApp = isInAppBrowser();
  const iosStandalone = isIOS() && isStandaloneMode();

  if (!storageOk || inApp) {
    throw new Error(
      "Google sign-in won’t work in this browser. Please open the site in Safari/Chrome (not WhatsApp/Instagram browser), turn off Private Browsing, and try again."
    );
  }

  try {
    const res = await signInWithPopup(auth, googleProvider);
    return {
      user: res?.user || null,
      isNewUser: !!res?.additionalUserInfo?.isNewUser,
    };
  } catch (e) {
    // ✅ Special message for iOS A2HS popup restrictions
    if (iosStandalone) {
      throw new Error(
        "Google popup is blocked in iPhone Add-to-Home-Screen mode. Please open this site in Safari (not the home screen app), sign in with Google, then you can return."
      );
    }

    throw new Error(
      "Google popup was blocked or closed. Please allow popups for this site, then try again."
    );
  }
}

/* ---------------- Email auth ---------------- */

export async function signUpWithEmail(email, password) {
  const res = await createUserWithEmailAndPassword(auth, email, password);
  try {
    await sendEmailVerification(res.user);
  } catch {}
  return { user: res.user, isNewUser: true };
}

export async function loginWithEmail(email, password) {
  const res = await signInWithEmailAndPassword(auth, email, password);
  return { user: res.user, isNewUser: false };
}

export async function logout() {
  await signOut(auth);
}

export async function resetPassword(email) {
  return await sendPasswordResetEmail(auth, email);
}

export async function resendVerificationEmail() {
  const u = auth.currentUser;
  if (!u) throw new Error("No current user");
  return await sendEmailVerification(u);
}

export async function refreshUser() {
  const u = auth.currentUser;
  if (!u) throw new Error("No current user");
  await reload(u);
  return u;
}